import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";
import { Logo } from "./Logo";
import { Avatar } from "./common";
import { GlobalPlayer } from "./GlobalPlayer";
import { CreateModal } from "./CreateModal";
import { RightRail } from "./RightRail";
import { InstallButton } from "./InstallButton";
import { InstallNudge } from "./InstallNudge";
import { api } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { toast } from "sonner";
import {
  Home, Compass, Film, Music2, Tv, Radio, BarChart3, Briefcase, MessageSquare,
  Bell, Wallet, Clapperboard, Settings, Search, Plus, Shield, Trophy, Star, LogOut, Handshake, Users, Gamepad2, ShoppingBag, Package, ShoppingCart, Store as StoreIcon, Menu, X, Library, Calendar, Building2
} from "lucide-react";

const NAV = [
  ["/home", Home, "Home"], ["/explore", Compass, "Explore"], ["/virtucity", Building2, "VirtuCity"], ["/reels", Film, "Reels"],
  ["/music", Music2, "Music"], ["/tv", Tv, "TV"], ["/live", Radio, "Live"],
  ["/shop", ShoppingBag, "Shop"],
  ["/charts", BarChart3, "Charts"], ["/industry", Briefcase, "Industry"],
  ["/crews", Users, "VCrews"],
  ["/events", Calendar, "Events"],
  ["/awards", Trophy, "Awards"], ["/messages", MessageSquare, "Messages"],
  ["/notifications", Bell, "Notifications"], ["/wallet", Wallet, "Wallet"],
  ["/studio", Clapperboard, "Creator Studio"], ["/fivem", Gamepad2, "FiveM Script"],
];

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const { current } = usePlayer();
  const [createOpen, setCreateOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const [q, setQ] = useState("");

  useEffect(() => {
    const load = () => {
      api.get("/notifications/unread-count").then(({ data }) => setUnread(data.count)).catch(() => {});
      api.get("/messages/unread-count").then(({ data }) => setMsgUnread(data.count)).catch(() => {});
    };
    load(); const i = setInterval(load, 20000); return () => clearInterval(i);
  }, [loc.pathname]);

  useRealtime((msg) => {
    const canPush = typeof Notification !== "undefined" && Notification.permission === "granted" && localStorage.getItem("vs_push") === "1";
    if (msg.event === "notification") {
      setUnread((u) => u + 1);
      toast(msg.data?.text || "New notification", { description: "VirtuStars" });
      if (canPush) try { new Notification("VirtuStars", { body: msg.data?.text || "New activity", icon: "/icon-192.png" }); } catch {}
      window.dispatchEvent(new CustomEvent("vs:notification", { detail: msg.data }));
    } else if (msg.event === "message") {
      window.dispatchEvent(new CustomEvent("vs:message", { detail: msg.data }));
      if (loc.pathname !== "/messages") {
        setMsgUnread((u) => u + 1);
        toast(`${msg.data?.text || "New message"}`, { description: "New message" });
        if (canPush) try { new Notification("New message", { body: msg.data?.text || "You have a new message", icon: "/icon-192.png" }); } catch {}
      }
    }
  });

  const submitSearch = (e) => { e.preventDefault(); if (q.trim()) nav(`/explore?q=${encodeURIComponent(q)}`); };
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);
  const playerPad = current ? "pb-[150px] md:pb-24" : "pb-24 md:pb-10";

  return (
    <div className="min-h-screen bg-[#030303]">
      {/* Desktop left nav */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 z-50 w-20 lg:w-64 flex-col border-r border-white/8 bg-[#06070b]">
        <div className="px-4 lg:px-5 h-16 flex items-center"><Logo withText={false} /><span className="hidden lg:block ml-2 font-display font-extrabold tracking-tight">VIRTU<span className="text-gold-grad">STARS</span></span></div>
        <nav className="flex-1 overflow-y-auto hide-scroll px-2 lg:px-3 py-2 space-y-0.5">
          {NAV.map(([to, Icon, label]) => (
            <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(" ", "-")}`}
              onClick={() => { if (to === "/home" && loc.pathname === "/home") window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors relative ${isActive ? "bg-white/8 text-white" : "text-muted-foreground hover:text-white hover:bg-white/5"}`}>
              <Icon size={20} className="shrink-0" />
              <span className="hidden lg:block">{label}</span>
              {label === "Notifications" && unread > 0 && (
                <span className="absolute left-7 lg:left-auto lg:right-3 top-2 lg:top-1/2 lg:-translate-y-1/2 min-w-[18px] h-[18px] px-1 rounded-full bg-eblue text-black text-[10px] font-bold flex items-center justify-center">{unread}</span>
              )}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink to="/admin" data-testid="nav-admin" className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-gold/15 text-gold" : "text-gold/70 hover:bg-gold/10"}`}>
              <Shield size={20} /><span className="hidden lg:block">Admin</span>
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-white/8 space-y-1">
          <div className="hidden lg:block mb-1"><InstallButton variant="sidebar" /></div>
          {user?.plan !== "plus" && (
            <button onClick={() => nav("/plus")} data-testid="nav-plus-upgrade"
              className="w-full btn-gold rounded-xl py-2.5 text-sm flex items-center justify-center gap-2"><Star size={16} /><span className="hidden lg:block">Get VirtuStars+</span></button>
          )}
          <NavLink to={`/${user?.username}`} className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/5">
            <Avatar src={user?.avatar_url} name={user?.display_name} size={34} />
            <div className="hidden lg:block min-w-0"><div className="text-sm font-semibold truncate">{user?.display_name}</div><div className="text-xs text-muted-foreground truncate">@{user?.username}</div></div>
          </NavLink>
          <div className="hidden lg:flex gap-1">
            <NavLink to="/settings" className="flex-1 flex items-center justify-center gap-2 px-2 py-2 rounded-xl text-xs text-muted-foreground hover:bg-white/5"><Settings size={15} /> Settings</NavLink>
            <button onClick={() => { logout(); nav("/"); }} data-testid="logout-btn" className="flex items-center justify-center px-3 py-2 rounded-xl text-muted-foreground hover:bg-white/5 hover:text-destructive"><LogOut size={15} /></button>
          </div>
        </div>
      </aside>

      {/* Mobile top header */}
      <header className="md:hidden sticky top-0 z-40 glass border-b border-white/8 h-14 flex items-center gap-1.5 px-2.5">
        <Logo withText={false} size={28} />
        <form onSubmit={submitSearch} className="flex-1 min-w-0 flex items-center gap-2 surface-2 rounded-full px-3 h-9">
          <Search size={15} className="text-muted-foreground shrink-0" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="bg-transparent flex-1 min-w-0 text-sm outline-none" data-testid="mobile-search-input" aria-label="Search" />
        </form>
        <NavLink to="/messages" aria-label="Messages" data-testid="mobile-header-messages"
          className={({ isActive }) => `relative shrink-0 w-9 h-9 flex items-center justify-center rounded-full ${isActive ? "text-eblue bg-eblue/10" : "text-white hover:bg-white/5"}`}>
          <MessageSquare size={20} />
          {msgUnread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">{msgUnread > 9 ? "9+" : msgUnread}</span>}
        </NavLink>
        <NavLink to="/notifications" aria-label="Notifications" data-testid="mobile-header-notifications"
          className={({ isActive }) => `relative shrink-0 w-9 h-9 flex items-center justify-center rounded-full ${isActive ? "text-eblue bg-eblue/10" : "text-white hover:bg-white/5"}`}>
          <Bell size={20} />
          {unread > 0 && <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-eblue" />}
        </NavLink>
        <button onClick={() => setMenuOpen(true)} aria-label="Menu" data-testid="mobile-hamburger-btn"
          className="relative shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-white hover:bg-white/5">
          <Menu size={22} />
        </button>
      </header>

      {/* Mobile hamburger menu */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMenuOpen(false)} />
          <div className="absolute right-0 top-0 bottom-0 w-[82%] max-w-xs bg-[#06070b] border-l border-white/10 flex flex-col animate-[slideIn_.2s_ease]">
            <div className="h-14 px-4 flex items-center justify-between border-b border-white/8">
              <span className="font-display font-extrabold tracking-tight">VIRTU<span className="text-gold-grad">STARS</span></span>
              <button onClick={() => setMenuOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/5"><X size={20} /></button>
            </div>
            <nav className="flex-1 overflow-y-auto hide-scroll p-2">
              {NAV.filter(([, , label]) => label !== "Messages" && label !== "Notifications").map(([to, Icon, label]) => (
                <NavLink key={to} to={to} onClick={() => setMenuOpen(false)} data-testid={`mobile-menu-${label.toLowerCase().replace(" ", "-")}`}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl text-sm transition-colors relative ${isActive ? "bg-white/8 text-white" : "text-muted-foreground hover:text-white hover:bg-white/5"}`}>
                  <Icon size={20} className="shrink-0" />{label}
                </NavLink>
              ))}
              {user?.role === "admin" && (
                <NavLink to="/admin" onClick={() => setMenuOpen(false)} data-testid="mobile-menu-admin"
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl text-sm ${isActive ? "bg-gold/15 text-gold" : "text-gold/80 hover:bg-gold/10"}`}>
                  <Shield size={20} />Admin
                </NavLink>
              )}
            </nav>
            <div className="p-3 border-t border-white/8 space-y-1">
              <NavLink to={`/${user?.username}`} onClick={() => setMenuOpen(false)} className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/5">
                <Avatar src={user?.avatar_url} name={user?.display_name} size={34} />
                <div className="min-w-0"><div className="text-sm font-semibold truncate">{user?.display_name}</div><div className="text-xs text-muted-foreground truncate">@{user?.username}</div></div>
              </NavLink>
              <div className="flex gap-1">
                <NavLink to="/settings" onClick={() => setMenuOpen(false)} className="flex-1 flex items-center justify-center gap-2 px-2 py-2 rounded-xl text-xs text-muted-foreground hover:bg-white/5"><Settings size={15} /> Settings</NavLink>
                <button onClick={() => { setMenuOpen(false); logout(); nav("/"); }} data-testid="mobile-menu-logout" className="flex items-center justify-center px-3 py-2 rounded-xl text-muted-foreground hover:bg-white/5 hover:text-destructive"><LogOut size={15} /></button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main */}
      <div className="md:pl-20 lg:pl-64">
        <div className="flex">
          <main className={`flex-1 min-w-0 ${playerPad}`}>
            {/* Desktop top search bar */}
            <div className="hidden md:flex sticky top-0 z-30 glass border-b border-white/8 h-16 items-center gap-3 px-6">
              <form onSubmit={submitSearch} className="flex items-center gap-2 surface-2 rounded-full px-4 h-10 w-full max-w-md">
                <Search size={16} className="text-muted-foreground" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search creators, music, shows, events…" className="bg-transparent flex-1 text-sm outline-none" data-testid="desktop-search-input" />
              </form>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={() => setCreateOpen(true)} data-testid="create-btn-desktop" className="btn-blue rounded-full px-5 h-10 flex items-center gap-2 text-sm"><Plus size={18} /> Create</button>
              </div>
            </div>
            <div className="max-w-[1100px] mx-auto w-full">{children}</div>
          </main>
          <RightRail />
        </div>
      </div>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-white/8 h-16 grid grid-cols-5 items-center px-2">
        <NavLink to="/home" onClick={() => { if (loc.pathname === "/home") window.scrollTo({ top: 0, behavior: "smooth" }); }} className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-eblue" : "text-muted-foreground"}`} data-testid="mnav-home"><Home size={21} />Home</NavLink>
        <NavLink to="/explore" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-eblue" : "text-muted-foreground"}`} data-testid="mnav-explore"><Compass size={21} />Explore</NavLink>
        <button onClick={() => setCreateOpen(true)} data-testid="create-btn-mobile" className="flex items-center justify-center"><span className="w-12 h-12 -mt-4 rounded-2xl btn-gold flex items-center justify-center shadow-lg"><Plus size={24} /></span></button>
        <NavLink to="/reels" className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-eblue" : "text-muted-foreground"}`} data-testid="mnav-reels"><Film size={21} />Reels</NavLink>
        <NavLink to={`/${user?.username}`} className={({ isActive }) => `flex flex-col items-center gap-0.5 text-[10px] ${isActive ? "text-eblue" : "text-muted-foreground"}`} data-testid="mnav-profile"><Avatar src={user?.avatar_url} name={user?.display_name} size={22} />Profile</NavLink>
      </nav>

      <GlobalPlayer />
      <InstallNudge />
      <CreateModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => window.dispatchEvent(new Event("vs:refresh"))} />
    </div>
  );
}
